<template>
  <FormGroup>
    <FormField label="Available move-in date" :error="form.fieldErrors('desiredLeasingDate')">
      <Input v-model="form.fields.desiredLeasingDate" type="date" />
    </FormField>

    <FormGroup gap="medium">
      <Label>Desired leasing term</Label>
      <FormField v-for="item in possibleLeaseTerms" :key="item" :label="item" direction="row" label-position="after">
        <Checkbox :value="item" v-model="form.fields.possibleLeaseTerms" />
      </FormField>
      <FormFieldErrors :error="form.fieldErrors('possibleLeaseTerms')" />
    </FormGroup>
  </FormGroup>
</template>

<script setup lang="ts">
import { required } from '@vuelidate/validators';
import { useDateFormat } from '@vueuse/core';
import { watch } from 'vue';

import { Checkbox, FormField, FormFieldErrors, FormGroup, Input, Label, useForm } from '@tallo/design-system';
import { PossibleLeaseTerms, PropertyLeaseConditionsDto } from '@tallo/property';

import { SaveLeaseConditionsDto } from '../../dtos/save-lease-condition.dto.interface';
import { usePropertyStore } from '../../stores/property.store';

const propertyStore = usePropertyStore();
const possibleLeaseTerms = Object.values(PossibleLeaseTerms);
const form = useForm<Pick<SaveLeaseConditionsDto, 'possibleLeaseTerms' | 'desiredLeasingDate'>>(
  {
    desiredLeasingDate: null,
    possibleLeaseTerms: [],
  },
  {
    desiredLeasingDate: { required },
    possibleLeaseTerms: { required },
  },
);

async function save() {
  return form.submit(async (fields) => {
    return propertyStore.saveLeaseConditions({
      ...fields,
      desiredLeasingDate: fields.desiredLeasingDate ? new Date(fields.desiredLeasingDate).toISOString() : null,
    });
  });
}

function populateForm(leaseConditions: PropertyLeaseConditionsDto) {
  const defaultDate = useDateFormat(new Date(), 'YYYY-MM-DD').value;

  Object.assign(form.fields, {
    desiredLeasingDate: leaseConditions?.desiredLeasingDate?.slice(0, 10) ?? defaultDate,
    possibleLeaseTerms: leaseConditions?.possibleLeaseTerms ?? [PossibleLeaseTerms.ONE_YEAR],
  });
}

watch(() => propertyStore.property?.leaseConditions!, populateForm, { immediate: true });

defineExpose({ save, form });
</script>
